import PlannedOrdersApi from "@/api/plannedOrdersApi"
import { AppThunk } from "./store"
import {
  assignPlannedOrderToDispatcher,
  assignPlannedOrderToVehicle,
  PlannedOrder,
  removePlannedOrder,
  setPlannedOrders,
  unassignPlannedOrder,
  upsertPlannedOrder,
} from "./plannedOrdersSlice"

// Local state is the optimistic source of truth; every mutation is pushed to
// the gps backend best-effort. A failed push is logged and will be reconciled
// by the next fetchPlannedOrders (server wins).
const pushOrder = (getOrder: () => PlannedOrder | undefined) => {
  const order = getOrder()
  if (!order || !window.XMPP) return
  PlannedOrdersApi.upsert(window.XMPP, order).catch((error) => {
    console.warn("Failed to push planned order to gps", error)
  })
}

// The pool has no live push channel (no PubSub node), so the store only ever
// changes when something explicitly fetches or mutates it. Any view rendering
// planned orders outside the planner therefore has to pull too, otherwise it
// keeps showing orders another dispatcher — or Navigator's own sync — already
// moved: seen live as a vehicle chat offering "Planowane zlecenia (1)" for an
// order that no longer existed server-side at all.
const PLANNED_ORDERS_MAX_AGE_MS = 60_000
let lastFetchedAtMs = 0
let fetchInFlight: Promise<void> | null = null

export const fetchPlannedOrders =
  (): AppThunk<Promise<void>> => async (dispatch) => {
    if (!window.XMPP) return
    try {
      const orders = await PlannedOrdersApi.fetchAll(window.XMPP)
      dispatch(setPlannedOrders({ orders }))
      lastFetchedAtMs = Date.now()
    } catch (error) {
      console.warn("Failed to fetch planned orders from gps", error)
    }
  }

// Same fetch for the callers that run on every chat switch: the whole pool
// travels as several paged IQs (190+ orders on Oskar's Navigator), so it is
// only worth re-pulling once the local copy is actually old.
export const fetchPlannedOrdersIfStale =
  (): AppThunk<Promise<void>> => async (dispatch) => {
    if (fetchInFlight) return fetchInFlight
    if (Date.now() - lastFetchedAtMs < PLANNED_ORDERS_MAX_AGE_MS) return
    fetchInFlight = dispatch(fetchPlannedOrders()).finally(() => {
      fetchInFlight = null
    })
    return fetchInFlight
  }

export const savePlannedOrder =
  (order: PlannedOrder): AppThunk =>
  (dispatch) => {
    dispatch(upsertPlannedOrder({ order }))
    pushOrder(() => order)
  }

// Stop enrichment (e.g. lazily geocoded coordinates) merges into the CURRENT
// order — never overwrites assignment state, which may have changed while the
// async enrichment (Google geocoder) was running.
export const enrichPlannedOrderStops =
  (id: string, stops: PlannedOrder["stops"]): AppThunk =>
  (dispatch, getState) => {
    const current = getState().plannedOrders.orders.find(
      (order) => order.id === id,
    )
    if (!current) return
    const merged = { ...current, stops }
    dispatch(upsertPlannedOrder({ order: merged }))
    pushOrder(() => merged)
  }

export const assignPlannedOrderToDispatcherAndSync =
  (id: string, dispatcherJid: string): AppThunk =>
  (dispatch, getState) => {
    dispatch(assignPlannedOrderToDispatcher({ id, dispatcherJid }))
    pushOrder(() =>
      getState().plannedOrders.orders.find((order) => order.id === id),
    )
  }

export const assignPlannedOrderToVehicleAndSync =
  (id: string, vehicleChatJid: string): AppThunk =>
  (dispatch, getState) => {
    dispatch(assignPlannedOrderToVehicle({ id, vehicleChatJid }))
    pushOrder(() =>
      getState().plannedOrders.orders.find((order) => order.id === id),
    )
  }

export const unassignPlannedOrderAndSync =
  (id: string): AppThunk =>
  (dispatch, getState) => {
    dispatch(unassignPlannedOrder({ id }))
    pushOrder(() =>
      getState().plannedOrders.orders.find((order) => order.id === id),
    )
  }

export const removePlannedOrderAndSync =
  (id: string): AppThunk =>
  (dispatch) => {
    dispatch(removePlannedOrder({ id }))
    if (!window.XMPP) return
    PlannedOrdersApi.remove(window.XMPP, id).catch((error) => {
      console.warn("Failed to delete planned order in gps", error)
    })
  }
